import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Modal } from '@/app/components/ui/Modal';
import { PageHeader } from '@/app/components/ui/PageHeader';
import {
  cancelSubscription,
  getHistory,
  getProducts,
  switchPlan,
  type SubscriptionHistoryEntry,
  type SubscriptionPlan,
  type SubscriptionProduct,
} from '@/features/subscription/api';
import { useSubscriptionStatus } from '@/features/subscription/useSubscriptionStatus';
import { formatCurrency, formatDate } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * `/subscription` — the caller's own Mollie subscription, per *Abonnement
 * (Mollie, voor de gebruiker zelf)*: status, plans, manage and history as
 * four stacked cards, alongside the Profiel left menu (`ProfileNav`). No
 * role required — every authenticated caller reaches this from the account
 * menu.
 */
export function SubscriptionPage() {
  const { t } = useTranslation();
  const entitlement = useSubscriptionStatus();
  const products = useAsync(() => getProducts(), []);
  const history = useAsync(() => getHistory(), []);

  // `entitlement.data` goes back to `null` for the instant a reload is in
  // flight (useAsync has no cache), which would otherwise unmount the Manage
  // card — and the "cancelled" confirmation it is showing — mid-flow.
  // "Adjusting state during render" (React's own pattern for deriving state
  // from a changed prop/value without an effect) keeps the last known active
  // flag across that gap.
  const [lastEntitlementData, setLastEntitlementData] = useState(entitlement.data);
  const [knownActive, setKnownActive] = useState(entitlement.data?.active ?? false);
  if (entitlement.data !== lastEntitlementData) {
    setLastEntitlementData(entitlement.data);
    if (entitlement.data) setKnownActive(entitlement.data.active ?? false);
  }

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('subscription.title')} lead={t('subscription.lead')} />

      <Card>
        <CardHeader title={t('subscription.status.title')} />
        <AsyncSection state={entitlement} label={t('subscription.title')}>
          {(data) => <StatusCard entitlement={data} />}
        </AsyncSection>
      </Card>

      <Card>
        <CardHeader title={t('subscription.plans.title')} />
        <AsyncSection state={products} label={t('subscription.plans.title')}>
          {(data) => (
            <PlansCard
              products={data.products ?? []}
              active={knownActive}
              currentTier={entitlement.data?.tier ?? null}
              onSwitched={() => {
                entitlement.reload();
                products.reload();
              }}
            />
          )}
        </AsyncSection>
      </Card>

      {knownActive && (
        <Card>
          <CardHeader title={t('subscription.manage.title')} />
          <ManageCard onCancelled={() => entitlement.reload()} />
        </Card>
      )}

      <Card padding="none">
        <div className="p-5 pb-0">
          <CardHeader title={t('subscription.history.title')} />
        </div>
        <AsyncSection state={history} label={t('subscription.history.title')}>
          {(data) => <HistoryCard purchases={data.purchases ?? []} />}
        </AsyncSection>
      </Card>
    </section>
  );
}

function StatusCard({
  entitlement,
}: {
  entitlement: NonNullable<ReturnType<typeof useSubscriptionStatus>['data']>;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={entitlement.active ? 'mint' : 'outline'}>
          {entitlement.active ? t('subscription.status.active') : t('subscription.status.inactive')}
        </Badge>
        {entitlement.tier && <span className="text-ink text-sm">{entitlement.tier}</span>}
      </div>
      {entitlement.expires_at && (
        <p className="text-ink text-sm">
          {t('subscription.status.expires')}: {formatDate(entitlement.expires_at)}
        </p>
      )}
      <p className="text-ink text-sm">
        {entitlement.auto_renews
          ? t('subscription.status.autoRenews')
          : t('subscription.status.doesNotRenew')}
      </p>
      {entitlement.auto_renews && entitlement.next_charge_at && (
        <p className="text-ink text-sm">
          {t('subscription.status.nextCharge')}: {formatDate(entitlement.next_charge_at)}
        </p>
      )}
      {entitlement.store_purchase_blocked && (
        <Alert tone="warning">
          {entitlement.store_purchase_blocked_reason ?? t('subscription.status.blocked')}
        </Alert>
      )}
    </div>
  );
}

function PlansCard({
  products,
  active,
  currentTier,
  onSwitched,
}: {
  products: SubscriptionProduct[];
  active: boolean;
  currentTier: string | null;
  onSwitched: () => void;
}) {
  const { t } = useTranslation();
  const [pendingSwitch, setPendingSwitch] = useState<SubscriptionPlan | null>(null);
  const switching = useSubmit(async (product: SubscriptionPlan) => {
    await switchPlan(product);
  });

  const mollieProducts = products.filter((product) => product.provider === 'mollie');

  if (mollieProducts.length === 0) {
    return <EmptyState text={t('subscription.plans.blocked')} />;
  }

  const confirmSwitch = async () => {
    if (!pendingSwitch) return;
    const ok = await switching.submit(pendingSwitch);
    if (ok) {
      setPendingSwitch(null);
      onSwitched();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {mollieProducts.map((product) => {
          const isCurrent = active && product.tier === currentTier;
          const priceLabel =
            product.price_amount != null && product.price_currency
              ? product.period_unit === 'year'
                ? t('subscription.plans.perYear', {
                    price: formatCurrency(product.price_amount, product.price_currency),
                  })
                : t('subscription.plans.perMonth', {
                    price: formatCurrency(product.price_amount, product.price_currency),
                  })
              : null;
          const plan: SubscriptionPlan =
            product.provider_product_id === 'yearly' ? 'yearly' : 'monthly';

          return (
            <div key={product.provider_product_id} className="border-line rounded-2xl border p-4">
              <p className="text-ink text-sm font-semibold">{product.tier}</p>
              {priceLabel && <p className="text-ink mt-1 text-sm">{priceLabel}</p>}
              <div className="mt-3">
                {isCurrent ? (
                  <Badge tone="mint">{t('subscription.plans.current')}</Badge>
                ) : active ? (
                  <Button variant="secondary" size="sm" onClick={() => setPendingSwitch(plan)}>
                    {plan === 'yearly'
                      ? t('subscription.plans.switchToYearly')
                      : t('subscription.plans.switchToMonthly')}
                  </Button>
                ) : (
                  <Button as={Link} to={`/subscription/checkout?plan=${plan}`} size="sm">
                    {t('subscription.plans.subscribe')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <SubmitError error={switching.error} />

      <Modal
        open={pendingSwitch !== null}
        onClose={() => setPendingSwitch(null)}
        title={
          pendingSwitch === 'yearly'
            ? t('subscription.plans.switchToYearly')
            : t('subscription.plans.switchToMonthly')
        }
      >
        <p className="text-ink text-sm">{t('subscription.plans.switchWarning')}</p>
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setPendingSwitch(null)}>
            {t('subscription.dialogCancel')}
          </Button>
          <Button disabled={switching.pending} onClick={() => void confirmSwitch()}>
            {t('subscription.plans.switchConfirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function ManageCard({ onCancelled }: { onCancelled: () => void }) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [accessUntil, setAccessUntil] = useState<string | null>(null);
  const cancel = useSubmit(async () => {
    const result = await cancelSubscription();
    setAccessUntil(result.access_until ?? null);
  });

  const confirmCancel = async () => {
    const ok = await cancel.submit();
    if (ok) {
      setConfirmOpen(false);
      onCancelled();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {accessUntil && (
        <Alert tone="info">
          {t('subscription.manage.cancelled', { date: formatDate(accessUntil) })}
        </Alert>
      )}
      <p className="text-accent text-[13px]">{t('subscription.manage.proRataNote')}</p>
      <div className="flex flex-wrap gap-3">
        <Button variant="danger" onClick={() => setConfirmOpen(true)}>
          {t('subscription.manage.cancel')}
        </Button>
        <Button as={Link} to="/subscription/withdraw" variant="secondary">
          {t('subscription.manage.withdraw')}
        </Button>
      </div>
      <p className="text-accent text-[13px]">{t('subscription.manage.withdrawHint')}</p>
      <SubmitError error={cancel.error} />

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('subscription.manage.cancel')}
      >
        <p className="text-ink text-sm">{t('subscription.manage.proRataNote')}</p>
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
            {t('subscription.dialogCancel')}
          </Button>
          <Button variant="danger" disabled={cancel.pending} onClick={() => void confirmCancel()}>
            {t('subscription.manage.cancelConfirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function HistoryCard({ purchases }: { purchases: SubscriptionHistoryEntry[] }) {
  const { t } = useTranslation();
  const stateLabels: Record<string, string> = {
    granted: t('subscription.history.state_granted'),
    refunded: t('subscription.history.state_refunded'),
    revoked: t('subscription.history.state_revoked'),
    charged_back: t('subscription.history.state_charged_back'),
    rejected: t('subscription.history.state_rejected'),
  };

  if (purchases.length === 0) {
    return (
      <div className="p-5 pt-0">
        <EmptyState text={t('subscription.history.empty')} />
      </div>
    );
  }

  return (
    <DataTable
      rowKey={(row) => row.purchase_id ?? ''}
      columns={[
        {
          key: 'date',
          header: t('subscription.history.date'),
          render: (row) => (row.created_at ? formatDate(row.created_at) : '—'),
        },
        {
          key: 'period',
          header: t('subscription.history.period'),
          render: (row) =>
            row.granted_from && row.granted_until
              ? `${formatDate(row.granted_from)} – ${formatDate(row.granted_until)}`
              : '—',
        },
        {
          key: 'channel',
          header: t('subscription.history.channel'),
          render: (row) => row.provider,
        },
        {
          key: 'state',
          header: t('subscription.history.state'),
          render: (row) => stateLabels[row.state ?? ''] ?? row.state,
        },
        {
          key: 'links',
          header: '',
          align: 'end',
          render: (row) => (
            <>
              {row.withdrawal_url && (
                <a className="text-accent" href={row.withdrawal_url}>
                  {t('subscription.history.withdrawLink')}
                </a>
              )}
              {row.management_url && (
                <a className="text-accent" href={row.management_url}>
                  {t('subscription.history.manageLink')}
                </a>
              )}
            </>
          ),
        },
      ]}
      rows={purchases}
    />
  );
}
